import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";
import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

type UnapproveScenarioForActionPlanOptions = {
  scenarioBuilderClient: ScenarioBuilderClient;
  actionPlanUuid: string;
  scenarioId: string;
};

type UseUnapproveScenarioForActionPlanOptions = {
  config?: UseMutationOptions<ActionPlanSummary, AxiosError, UnapproveScenarioVariables>;
};

type UnapproveScenarioVariables = Omit<
  UnapproveScenarioForActionPlanOptions,
  "scenarioBuilderClient"
>;

const unapproveScenarioForActionPlan = ({
  scenarioBuilderClient,
  actionPlanUuid,
  scenarioId,
}: UnapproveScenarioForActionPlanOptions) => {
  return scenarioBuilderClient.actionPlans.unapproveScenarioForActionPlan({
    actionPlanUuid,
    unapproveScenarioRequest: {
      scenarioId,
    },
  });
};

export const useUnapproveScenarioForActionPlan = ({
  config,
}: UseUnapproveScenarioForActionPlanOptions = {}) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionPlanUuid, scenarioId }: UnapproveScenarioVariables) =>
      unapproveScenarioForActionPlan({ scenarioBuilderClient, actionPlanUuid, scenarioId }),

    onSuccess: async ({ uuid: actionPlanUuid }: ActionPlanSummary) => {
      toast.success("Scenario unapproved successfully!");
      if (actionPlanUuid) {
        await queryClient.invalidateQueries({ queryKey: ["action-plan", actionPlanUuid] });
      }
      await queryClient.invalidateQueries({ queryKey: ["action-plans"] });
      await queryClient.invalidateQueries({ queryKey: ["scenario-v2"] });
      await queryClient.invalidateQueries({ queryKey: ["scenario-results-v2"] });
    },
    onError: (error) => {
      toast.error("Failed to unapprove scenario. Please try again.");
      console.error("useUnapproveScenarioForActionPlan onError", error);
    },
    ...config,
  });
};
